<script setup>
/**
 * The Kept shelf (mystic page 2026-10-29, MIRROR-OUTLINE.md "Kept, in the
 * Journal"; mocks 23/24). Minimal, from the Journal's built pieces only: the
 * day page's own frame (the kicker "Kept", the title "What spoke to you", the
 * flourish), a "Five things · newest first" rule, then each kept thing as the
 * built taped-photo moment (JournalMoment, kind 'msg'): its date in the time
 * column, the giver's mark and "You kept Founder Lee’s drop", the picture
 * with its title. A tap opens the day it was kept; "Remove from Kept" sits in
 * the line's ⋯ and never deletes anything at the source. No grid, no filters.
 * On a computer it is the right page; on a phone a page of its own, with
 * ☰ Contents to go back.
 */
import { computed } from 'vue'
import JournalGlyph from './JournalGlyph.vue'
import JournalMoment from './JournalMoment.vue'
import { keptRule } from '@/utils/journal-kept'

const props = defineProps({
  kept: { type: Object, default: null },        // normalizeKept
  loading: { type: Boolean, default: false },
  desktop: { type: Boolean, default: false },
  menu: { type: String, default: '' },          // which ⋯ is open ('kept-page' or a kept id)
  calOpen: { type: Boolean, default: false },
  backLabel: { type: String, default: '' },
})
const emit = defineEmits(['goto', 'unkeep', 'menu', 'cal', 'search', 'back'])

const items = computed(() => (props.kept && props.kept.items) || [])
function pageMenu () { emit('menu', props.menu === 'kept-page' ? '' : 'kept-page') }
function back () { emit('menu', ''); emit('back') }
</script>

<template>
  <div class="kv" :class="{ desk: desktop }" data-testid="journal-kept">
    <header class="ph">
      <div class="phrow">
        <div class="wk">Kept</div>
        <div class="tools">
          <template v-if="!desktop">
            <button type="button" class="ic" aria-label="Contents" title="Contents" :aria-expanded="calOpen" data-testid="journal-cal-btn" @click.stop="emit('cal')"><JournalGlyph name="contents" /></button>
            <button type="button" class="ic" aria-label="Search your journal" data-testid="journal-search-btn" @click.stop="emit('search')"><JournalGlyph name="search" /></button>
          </template>
          <span class="menu-wrap">
            <button type="button" class="ic" aria-label="This page’s options" :aria-expanded="menu === 'kept-page'" data-testid="journal-kept-menu" @click.stop="pageMenu"><JournalGlyph name="more" /></button>
            <div v-if="menu === 'kept-page'" class="pop" role="menu" @click.stop>
              <button type="button" role="menuitem" data-testid="journal-view-back" @click="back">Back to {{ backLabel || 'the day' }}</button>
            </div>
          </span>
        </div>
      </div>
      <h2 class="dt">What spoke to you</h2>
      <svg class="flour" viewBox="0 0 320 10" preserveAspectRatio="none" aria-hidden="true"><path class="ink" pathLength="1" stroke-width="1.2" d="M2 6C60 2 120 8 170 5s110-4 148-1" /></svg>
    </header>

    <p v-if="loading && !items.length" class="hidnote">Opening what you kept…</p>
    <p v-else-if="!items.length" class="hidnote">Nothing kept yet. Keep a drop, a lesson’s picture or a card and it waits here.</p>
    <template v-else>
      <div class="rule">{{ keptRule(items.length) }}</div>
      <JournalMoment v-for="it in items" :key="it.id" :m="it.moment" :desktop="desktop" kept :menu-open="menu === it.id"
                     @menu="emit('menu', $event)" @open-msg="emit('goto', it.key)" @unkeep="emit('unkeep', it)" />
    </template>
  </div>
</template>

<style scoped>
.kv { color: var(--j-ink); }
.ph { position: sticky; top: 0; z-index: 5; will-change: transform; margin: 0 -16px 0 -18px; padding: 10px 16px 2px 18px; background: #f2e9d6; }
.ph::after { content: ''; position: absolute; left: 0; right: 0; top: 100%; height: 16px; pointer-events: none; background: linear-gradient(rgba(242, 233, 214, .985), rgba(242, 233, 214, 0)); }
.desk .ph { margin: 0 -30px; padding: 16px 30px 2px; }
.phrow { display: flex; align-items: center; justify-content: space-between; min-height: 34px; padding-right: 30px; }
.desk .phrow { padding-right: 40px; }
.tools { display: flex; align-items: center; }
.wk { font: 600 10.5px var(--j-display); letter-spacing: .22em; text-transform: uppercase; color: var(--j-hand); }
.dt { margin: 0; font: 600 31px/1.08 var(--j-serif); color: var(--j-ink); }
.desk .dt { font-size: 34px; }
.ic { width: 40px; height: 40px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; color: var(--j-ink2); background: none; border: 0; padding: 0; cursor: pointer; }
.ic:hover { background: rgba(42, 24, 69, .06); }
.ic :deep(svg) { width: 19px; height: 19px; }
.flour { display: block; width: 100%; height: 10px; margin: 4px 0 0; color: var(--j-hand); }
.ink { fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; }
.menu-wrap { position: relative; }
.pop { position: absolute; z-index: 9; right: 0; top: 100%; min-width: 220px; padding: 6px; border-radius: 10px; background: #fbf6ea; box-shadow: 0 10px 30px rgba(42, 24, 69, .3), 0 0 0 1px rgba(42, 24, 69, .1); }
.pop button { display: block; width: 100%; text-align: left; padding: 10px 10px; border-radius: 7px; font: 500 14px var(--j-ui); color: var(--j-ink); background: none; border: 0; cursor: pointer; }
.pop button:hover { background: rgba(42, 24, 69, .07); }
.rule { display: flex; align-items: center; gap: 10px; margin: 16px 0 2px; font: 600 9.5px var(--j-display); letter-spacing: .18em; text-transform: uppercase; color: var(--j-ink3); }
.rule::after { content: ''; flex: 1; height: 1px; background: rgba(42, 24, 69, .12); }
.hidnote { margin: 16px 0 0; font: italic 500 16px/1.4 var(--j-serif); color: var(--j-ink3); }
/* The time column holds a date here: room for "Oct 29". */
.kv :deep(.mo) { grid-template-columns: 44px minmax(0, 1fr) 26px; }
.kv.desk :deep(.mo) { grid-template-columns: 50px minmax(0, 1fr) 28px; }
</style>
